/** @jsx isolateComponent */

/* eslint-disable import/no-duplicates */
import type { ReactNode } from 'react';
import React, { Fragment } from 'react';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import {
  Button,
  Colors,
  Heading,
  HeadingLevel,
  Icon,
  Link,
  Text,
} from '@zapier/design-system';

import UniversalNavLink from '../UniversalNavLink';
import { usageLink } from '../../constants';
import { useUniversalNavContext } from '../../hooks';

import UniversalSidebarError from './UniversalSidebarError';
import UniversalSidebarSubheaderButton from './UniversalSidebarSubheaderButton';
import UniversalSidebarUsage from './UniversalSidebarUsage/UniversalSidebarUsage';
import useSidebarData from './utils/useSidebarData';
import UniversalSidebar, { isLinkSelected } from './UniversalSidebar';
import type { Props as UniversalSidebarProps } from './UniversalSidebar';

const infinitize = (number: number) => {
  return number === -1 ? Infinity : number;
};

type Props = Omit<
  UniversalSidebarProps,
  | 'isFullWidth'
  | 'isPinned'
  | 'links'
  | 'logoAriaLabel'
  | 'logoHref'
  | 'renderFooter'
  | 'skipLinkHref'
> & {
  /**
   * The `id` of the account the user is currently switched to.
   */
  currentAccountId: number;
};

type MaybeCtaProps = {
  numTasks: number;
  numTasksMax: number;
  numZaps: number;
  numZapsMax: number;
  planAmount: number;
  upgradeLink: string | undefined;
};

const MaybeCta = (props: MaybeCtaProps) => {
  const isFree = props.planAmount === 0;
  const isNearTaskLimit = props.numTasks / props.numTasksMax >= 0.8;
  const isNearZapLimit = props.numZaps / props.numZapsMax >= 0.8;
  return isFree || isNearTaskLimit || isNearZapLimit ? (
    <Button
      color="secondary"
      href={props.upgradeLink}
      isFullWidth={true}
      size="small"
    >
      Upgrade plan
    </Button>
  ) : null;
};

const Styles = labelStyles('UniversalSidebarContainer', {
  footerWrapper: {
    overflow: 'hidden',
    padding: 10,
  },
  tOSWrapper: {
    whiteSpace: 'initial',
    background: Colors.neutral200,
    border: `1px solid ${Colors.neutral300}`,
    borderRadius: 10,
    padding: 15,
  },
  tOSHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  tOSHeading: {
    display: 'flex',
    alignItems: 'center',
  },
  tOSDismissalIconButton: {
    display: 'flex',
    alignItems: 'center',
    '&:focus': {
      outline: `1px solid ${Colors.blue}`,
    },
  },
  tOSDismissalButton: {
    color: `${Colors.blue}`,
    textDecoration: 'underline',
    '&:focus': {
      outline: `1px solid ${Colors.blue}`,
    },
  },
  divider: {
    border: `1px solid ${Colors.neutral300}`,
    marginTop: 10,
    marginBottom: 10,
  },
});

const MaybeTOS = () => {
  const LATEST_VERSION_OF_TERMS = 'design-system-tos-notification-2022-11-15';
  const hasUserDismissedLatestToS =
    localStorage.getItem('versionOfTermsDismissed') ===
      LATEST_VERSION_OF_TERMS || false;

  const handleDismiss = () => {
    localStorage.setItem('versionOfTermsDismissed', LATEST_VERSION_OF_TERMS);
  };

  const { getFullyQualifiedURL, shouldRenderTOS } = useUniversalNavContext();
  return shouldRenderTOS && hasUserDismissedLatestToS === false ? (
    <Fragment>
      <hr css={Styles.divider} />
      <div css={Styles.tOSWrapper}>
        <div css={Styles.tOSHeader}>
          <div css={Styles.tOSHeading}>
            <Icon
              ariaHidden={true}
              color="blueJeans"
              name="alertInfoFill"
              size={18}
            />
            <HeadingLevel>
              <Text margin="0 0 0 5px" tag={Heading} type="smallPrint1Semibold">
                Updated Terms
              </Text>
            </HeadingLevel>
          </div>
          <button
            aria-label="Dismiss notification"
            css={Styles.tOSDismissalIconButton}
            onClick={handleDismiss}
            type="button"
          >
            <Icon ariaHidden={true} color="neutral800" name="formX" size={24} />
          </button>
        </div>
        <Text tag="p" type="smallPrint1">
          We updated the{' '}
          <Link href={getFullyQualifiedURL('/legal/terms-of-service')}>
            Zapier Terms of Service
          </Link>{' '}
          on November 15, 2022. For existing users, these updated terms will be
          effective beginning on December 15, 2022. By continuing to use Zapier,
          you agree to these updated terms.{' '}
          <button
            css={Styles.tOSDismissalButton}
            type="button"
            onClick={handleDismiss}
          >
            Dismiss
          </button>
        </Text>
      </div>
    </Fragment>
  ) : null;
};

const FooterWrapper = ({ children }: { children: ReactNode }) => (
  <div css={Styles.footerWrapper}>{children}</div>
);

/**
 * The hooked up `UniversalSidebar` component. Hooked up to data,
 * performs business logic, etc.
 */
const UniversalSidebarContainer = ({ currentAccountId, ...props }: Props) => {
  const { useQuery, getFullyQualifiedURL } = useUniversalNavContext();
  const result = useSidebarData({ accountId: currentAccountId, useQuery });
  const { loading, data, error } = result;

  const renderedUsageLink = (
    <UniversalNavLink
      href={usageLink.href}
      icon={usageLink.icon}
      isCollapsed={props.isCollapsed}
      isSelected={isLinkSelected(usageLink.href || '', props.selectedHref)}
      isSidebar={true}
      text={usageLink.text}
    />
  );

  const renderFooter = () => {
    if (props.isCollapsed) {
      return renderedUsageLink;
    }
    if (error) {
      return (
        <React.Fragment>
          {renderedUsageLink}
          <FooterWrapper>
            <UniversalSidebarError>{error.message}</UniversalSidebarError>
            <MaybeTOS />
          </FooterWrapper>
        </React.Fragment>
      );
    }
    if (loading || !data?.account) {
      return null;
    }
    const {
      plan,
      usage,
      isTrialing,
      trialEndDate,
      isTrialingTeams,
      teamTrialEndDate,
      isTrialingCompanies,
      companiesTrialEndDate,
    } = data.account;
    const tiers = plan.tiers;
    const freeTier = tiers && tiers.find((tier) => tier.pricePerTask === 0);
    const numFreeTasksMax = freeTier ? freeTier.numTasks : 0;
    const normalizedUsage = {
      isPlanMetered: plan.hasMeteredBilling,
      numTasks: usage.tasks.total,
      numFreeTasksMax,
      numTasksMax: infinitize(usage.tasks.limit),
      numZaps: usage.zaps.on,
      numZapsMax: infinitize(usage.zaps.limit),
    };
    const trialPlanName = isTrialingTeams
      ? 'Team'
      : isTrialingCompanies
      ? 'Company'
      : isTrialing
      ? 'Professional'
      : undefined;
    const trialPlanEndDate = isTrialingTeams
      ? teamTrialEndDate
      : isTrialingCompanies
      ? companiesTrialEndDate
      : trialEndDate;

    return (
      <FooterWrapper>
        <UniversalSidebarUsage
          {...normalizedUsage}
          resetDate={usage.periodEnd}
          title={`${plan.name} Plan`}
          trialPlanName={trialPlanName}
          trialPlanEndDate={trialPlanEndDate ?? undefined}
        >
          <MaybeCta
            {...normalizedUsage}
            planAmount={plan.amount}
            upgradeLink={getFullyQualifiedURL('/app/pricing')}
          />
          <MaybeTOS />
        </UniversalSidebarUsage>
      </FooterWrapper>
    );
  };

  return (
    <UniversalSidebar
      {...props}
      logoAriaLabel="Go to dashboard"
      logoHref={getFullyQualifiedURL('/app/dashboard')}
      renderFooter={renderFooter}
      renderSubheader={() => (
        <UniversalSidebarSubheaderButton
          href={
            getFullyQualifiedURL(
              '/webintent/create-zap?entry-point-method=make_a_zap_call_to_action'
            ) as string
          }
          iconName="formAdd"
          isCollapsed={props.isCollapsed}
          text="Create Zap"
        />
      )}
    />
  );
};

export default UniversalSidebarContainer;
